import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CustomerWithStats,
  CustomerStats,
  CustomerFilters,
  CustomerTag,
  CustomerStatus,
  CustomerBooking,
  CustomerNote,
} from "../types/customer";
import { customerService } from "../services/customerService";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";

import {
  FiSearch,
  FiDownload,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiDollarSign,
  FiTag,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiAlertCircle,
  FiPhoneCall,
  FiSend,
  FiFilePlus,
  FiFlag,
} from "react-icons/fi";

// Icon wrapper to handle react-icons type issues
type IconProps = {
  as: unknown;
  size?: number;
  className?: string;
};

const Icon = ({ as, size, className }: IconProps): React.ReactElement => {
  return React.createElement(as as any, { size, className }) as React.ReactElement;
};

const AVAILABLE_TAGS: CustomerTag[] = [
  'vip',
  'repeated',
  'corporate',
  'first-time',
  'problem',
  'damage',
  'chargeback',
  'no-show',
];

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState<CustomerWithStats[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithStats | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteCustomer, setNoteCustomer] = useState<CustomerWithStats | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<CustomerNote['type']>('general');
  const [savingNote, setSavingNote] = useState(false);
  const [customerNotes, setCustomerNotes] = useState<CustomerNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [lastBookingDetails, setLastBookingDetails] = useState<CustomerBooking | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [savingInternalNotes, setSavingInternalNotes] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [savingTags, setSavingTags] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState<CustomerFilters>({
    search: "",
    tag: "all",
    status: "all",
    sortBy: "createdAt",
    sortOrder: "desc",
    page: 1,
    limit: 20,
    minBookings: undefined,
    maxBookings: undefined,
    minSpent: undefined,
    maxSpent: undefined,
    lastBookingFrom: "",
    lastBookingTo: "",
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters((prev) => {
        if ((prev.search || '') === searchInput) {
          return prev;
        }

        return {
          ...prev,
          search: searchInput,
          page: 1,
        };
      });
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [customersRes, statsRes] = await Promise.all([
        customerService.getCustomersWithStats(filters),
        customerService.getCustomerStats(),
      ]);

      setCustomers(customersRes.customers || []);
      setTotalCount(customersRes.total || 0);
      setStats(statsRes);
    } catch (error) {
      console.error("Failed to load customer data:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleTagFilter = (tag: CustomerTag | "all") => {
    setFilters((prev) => ({ ...prev, tag, page: 1 }));
  };

  const handleStatusFilter = (status: CustomerStatus | "all") => {
    setFilters((prev) => ({ ...prev, status, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleViewCustomer = (customer: CustomerWithStats) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
    setInternalNotes(customer.internalNotes || '');
    loadCustomerDetails(customer._id);
  };

  const loadCustomerDetails = async (customerId: string) => {
    try {
      setDetailsLoading(true);
      setNotesLoading(true);
      const [bookings, notes] = await Promise.all([
        customerService.getCustomerBookingHistory(customerId),
        customerService.getCustomerNotes(customerId),
      ]);
      const sorted = [...bookings].sort((a, b) => {
        const aDate = new Date(a.pickUpDate || a.createdAt).getTime();
        const bDate = new Date(b.pickUpDate || b.createdAt).getTime();
        return bDate - aDate;
      });
      setLastBookingDetails(sorted[0] || null);
      setCustomerNotes(notes);
    } catch (error) {
      console.error('Failed to load customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setDetailsLoading(false);
      setNotesLoading(false);
    }
  };

  const handleManageTags = (customer: CustomerWithStats) => {
    setSelectedCustomer(customer);
    setSelectedTags(customer.tags || []);
    setShowTagModal(true);
  };

  const handleOpenNoteModal = (customer: CustomerWithStats) => {
    setNoteCustomer(customer);
    setNoteText('');
    setNoteType('general');
    setShowNoteModal(true);
  };

  const handleToggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSaveTags = async () => {
    if (!selectedCustomer) return;
    
    try {
      setSavingTags(true);
      await customerService.updateTags(selectedCustomer._id, selectedTags);
      toast.success('Tags updated successfully');
      setShowTagModal(false);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Failed to update tags:', error);
      toast.error('Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  const handleDeleteCustomer = (customer: CustomerWithStats) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  };

  const handleSaveInternalNotes = async () => {
    if (!selectedCustomer) return;
    try {
      setSavingInternalNotes(true);
      await customerService.updateInternalNotes(selectedCustomer._id, internalNotes);
      toast.success('Internal notes saved');
      loadData();
    } catch (error) {
      console.error('Failed to save internal notes:', error);
      toast.error('Failed to save internal notes');
    } finally {
      setSavingInternalNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteCustomer || !noteText.trim()) return;
    try {
      setSavingNote(true);
      const createdBy = user?.name || user?.email || 'admin';
      await customerService.addCustomerNote(noteCustomer._id, noteText.trim(), createdBy, noteType);
      toast.success('Note added');
      setShowNoteModal(false);
      setNoteText('');
      if (selectedCustomer && selectedCustomer._id === noteCustomer._id) {
        loadCustomerDetails(noteCustomer._id);
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      setDeleting(true);
      await customerService.deleteCustomer(selectedCustomer._id);
      toast.success(`Customer "${getCustomerName(selectedCustomer)}" deleted permanently`);
      setShowDeleteModal(false);
      setShowDetailsModal(false);
      setSelectedCustomer(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error('Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    customerService.exportToCSV(customers);
    toast.success("Customers exported to CSV");
  };

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  const formatDateTime = (date: string | Date | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null || amount === 0) return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  const getStatusBadge = (customer: CustomerWithStats) => {
    const status = customer.customerStatus || customer.status || "new";
    if (customer.isBlacklisted || status === "blacklisted") return "bg-red-100 text-red-700";
    if (status === "vip") return "bg-purple-100 text-purple-700";
    if (status === "active") return "bg-green-100 text-green-700";
    if (status === "inactive") return "bg-gray-100 text-gray-700";
    return "bg-blue-100 text-blue-700";
  };

  const getLicenseBadge = (expiry?: string) => {
    if (!expiry) return { label: "No Expiry", className: "bg-gray-100 text-gray-600" };
    const now = new Date();
    const exp = new Date(expiry);
    const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: "Expired", className: "bg-red-100 text-red-700" };
    if (diffDays <= 30) return { label: `Expiring in ${diffDays}d`, className: "bg-orange-100 text-orange-700" };
    return { label: `Valid (${diffDays}d)`, className: "bg-green-100 text-green-700" };
  };

  const getRiskFlags = (customer: CustomerWithStats) => {
    const tags = new Set(customer.tags || []);
    const flags: string[] = [];
    if (customer.isBlacklisted) flags.push("Blacklisted");
    if (tags.has("problem")) flags.push("Problem");
    if (tags.has("damage")) flags.push("Damage");
    if (tags.has("chargeback")) flags.push("Chargeback");
    if (tags.has("no-show")) flags.push("No-Show");
    return flags;
  };

  const getCustomerInitials = (customer: CustomerWithStats) => {
    const first = customer.firstName?.[0] || '';
    const last = customer.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getCustomerName = (customer: CustomerWithStats) => {
    const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
    return name || 'Unknown';
  };

  const limit = filters.limit || 20;
  const page = filters.page || 1;
  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <p className="text-gray-600">Manage and view customer information</p>
        </div>

        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Icon as={FiDownload} size={18} />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Icon as={FiUser} className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Customers</p>
                <p className="text-xl font-bold text-gray-900">{stats.totalCustomers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Icon as={FiCalendar} className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">New This Month</p>
                <p className="text-xl font-bold text-gray-900">{stats.newThisMonth}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Icon as={FiTag} className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">VIP Customers</p>
                <p className="text-xl font-bold text-gray-900">{stats.vipCustomers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Icon as={FiDollarSign} className="text-orange-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Repeat Customers</p>
                <p className="text-xl font-bold text-gray-900">{stats.repeatCustomers}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Icon as={FiSearch} className="text-gray-400" size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchInput}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <select
            value={filters.tag}
            onChange={(e) => handleTagFilter(e.target.value as CustomerTag | "all")}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">All Tags</option>
            <option value="vip">VIP</option>
            <option value="repeated">Repeated</option>
            <option value="corporate">Corporate</option>
            <option value="first-time">First Time</option>
            <option value="problem">Problem</option>
            <option value="damage">Damage</option>
            <option value="chargeback">Chargeback</option>
            <option value="no-show">No-Show</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleStatusFilter(e.target.value as CustomerStatus | "all")}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="active">Active</option>
            <option value="vip">VIP</option>
            <option value="inactive">Inactive</option>
            <option value="blacklisted">Blacklisted</option>
          </select>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 items-center">
          <input
            type="date"
            value={filters.lastBookingFrom || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, lastBookingFrom: e.target.value, page: 1 }))}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <input
            type="date"
            value={filters.lastBookingTo || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, lastBookingTo: e.target.value, page: 1 }))}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Customer Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex justify-center">
              <Icon as={FiUser} className="text-gray-300" size={48} />
            </div>
            <p className="mt-4 text-gray-500">No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Booking
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risk
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quick Actions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {customers.map((customer) => (
                  <tr key={customer._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <span className="text-orange-600 font-medium">
                            {getCustomerInitials(customer)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {getCustomerName(customer)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {customer.licenseNumber || "No license"}
                          </p>
                          <div className="mt-1">
                            {(() => {
                              const badge = getLicenseBadge(customer.licenseExpiryDate);
                              return (
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${badge.className}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Icon as={FiMail} size={14} />
                          <span className="truncate max-w-[200px]">{customer.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Icon as={FiPhone} size={14} />
                          {customer.contact || "N/A"}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`font-medium ${(customer.bookingCount || 0) > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                        {customer.bookingCount || 0}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`font-medium ${(customer.totalSpent || 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {formatCurrency(customer.totalSpent)}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-gray-600">{formatDate(customer.lastBookingDate)}</span>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${getStatusBadge(customer)}`}>
                        {customer.isBlacklisted ? "blacklisted" : (customer.customerStatus || customer.status || "new")}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      {(() => {
                        const flags = getRiskFlags(customer);
                        return flags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {flags.map((flag) => (
                              <span
                                key={flag}
                                className={`px-2 py-0.5 text-xs rounded-full ${
                                  flag === "Blacklisted"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-orange-100 text-orange-700"
                                }`}
                              >
                                {flag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">None</span>
                        );
                      })()}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 items-center">
                        {customer.tags && customer.tags.length > 0 ? (
                          customer.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className={`px-2 py-1 text-xs rounded-full ${
                                tag === "vip"
                                  ? "bg-purple-100 text-purple-700"
                                  : tag === "problem"
                                  ? "bg-red-100 text-red-700"
                                  : tag === "damage"
                                  ? "bg-rose-100 text-rose-700"
                                  : tag === "chargeback"
                                  ? "bg-orange-100 text-orange-700"
                                  : tag === "no-show"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : tag === "first-time"
                                  ? "bg-green-100 text-green-700"
                                  : tag === "corporate"
                                  ? "bg-blue-100 text-blue-700"
                                  : tag === "repeated"
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No tags</span>
                        )}

                        {customer.isBlacklisted && (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                            Blacklisted
                          </span>
                        )}
                        
                        <button
                          onClick={() => handleManageTags(customer)}
                          className="ml-1 p-1 text-gray-400 hover:text-orange-500 transition-colors"
                          title="Manage tags"
                        >
                          <Icon as={FiPlus} size={14} />
                        </button>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <a
                          href={customer.contact ? `tel:${customer.contact}` : undefined}
                          className={`p-1.5 rounded transition-colors ${
                            customer.contact ? 'text-gray-500 hover:text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title="Call customer"
                        >
                          <Icon as={FiPhoneCall} size={16} />
                        </a>
                        <a
                          href={customer.email ? `mailto:${customer.email}` : undefined}
                          className={`p-1.5 rounded transition-colors ${
                            customer.email ? 'text-gray-500 hover:text-blue-600 hover:bg-blue-50' : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title="Email customer"
                        >
                          <Icon as={FiSend} size={16} />
                        </a>
                        <button
                          onClick={() => navigate(`/bookings?customerId=${customer._id}`)}
                          className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Create booking"
                        >
                          <Icon as={FiFilePlus} size={16} />
                        </button>
                        <button
                          onClick={() => handleOpenNoteModal(customer)}
                          className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Add note"
                        >
                          <Icon as={FiFlag} size={16} />
                        </button>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewCustomer(customer)}
                          className="text-orange-500 hover:text-orange-600 font-medium text-sm"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleDeleteCustomer(customer)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Delete customer"
                        >
                          <Icon as={FiTrash2} size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalCount)} of{" "}
              {totalCount} customers
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <Icon as={FiChevronLeft} size={18} />
              </button>

              <span className="px-4 py-2 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <Icon as={FiChevronRight} size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Customer Details Modal */}
      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Customer Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon as={FiX} size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-bold text-xl">
                    {getCustomerInitials(selectedCustomer)}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {getCustomerName(selectedCustomer)}
                  </h3>
                  <p className="text-gray-500">{selectedCustomer.email || 'No email'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedCustomer.contact || "N/A"}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">License Number</p>
                  <p className="font-medium">{selectedCustomer.licenseNumber || "N/A"}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Bookings</p>
                  <p className="font-medium text-blue-600">{selectedCustomer.bookingCount || 0}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Spent</p>
                  <p className="font-medium text-green-600">
                    {formatCurrency(selectedCustomer.totalSpent)}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Last Booking</p>
                  <p className="font-medium">{formatDate(selectedCustomer.lastBookingDate)}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Customer Since</p>
                  <p className="font-medium">{formatDate(selectedCustomer.createdAt)}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg col-span-2">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">{selectedCustomer.address || "N/A"}</p>
                </div>
              </div>

              {/* Last Booking Details */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Last Booking Details</p>
                </div>
                {detailsLoading ? (
                  <div className="p-4 bg-gray-50 rounded-lg">Loading...</div>
                ) : lastBookingDetails ? (
                  <div className="p-4 bg-gray-50 rounded-lg grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Booking Ref</p>
                      <p className="font-medium">{lastBookingDetails.bookingReference || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="font-medium">{lastBookingDetails.status || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Vehicle</p>
                      <p className="font-medium">
                        {lastBookingDetails.car
                          ? `${lastBookingDetails.car.brand || ''} ${lastBookingDetails.car.model || ''} ${lastBookingDetails.car.registrationNumber || ''}`.trim()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Dates</p>
                      <p className="font-medium">
                        {formatDate(lastBookingDetails.pickUpDate)} → {formatDate(lastBookingDetails.returnDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="font-medium">{formatCurrency(lastBookingDetails.totalPrice)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Payment</p>
                      <p className="font-medium">{lastBookingDetails.paymentStatus || 'N/A'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg text-gray-500">No bookings found</div>
                )}
              </div>

              {/* Internal Notes */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Internal Notes & Flags</p>
                </div>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full min-h-[90px] p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Add internal notes, warnings, or operational flags..."
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleSaveInternalNotes}
                    disabled={savingInternalNotes}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                  >
                    {savingInternalNotes ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>

              {/* Notes Log */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Notes Log</p>
                  <button
                    onClick={() => handleOpenNoteModal(selectedCustomer)}
                    className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
                  >
                    <Icon as={FiPlus} size={14} />
                    Add Note
                  </button>
                </div>
                {notesLoading ? (
                  <div className="p-4 bg-gray-50 rounded-lg">Loading...</div>
                ) : customerNotes.length > 0 ? (
                  <div className="space-y-2">
                    {customerNotes.map((note) => (
                      <div key={note._id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {note.type} • {formatDateTime(note.createdAt)} • {note.createdBy}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">{note.note}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg text-gray-500">No notes yet</div>
                )}
              </div>

              {/* Tags Section */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Tags</p>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleManageTags(selectedCustomer);
                    }}
                    className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
                  >
                    <Icon as={FiEdit2} size={14} />
                    Manage Tags
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedCustomer.tags && selectedCustomer.tags.length > 0 ? (
                    selectedCustomer.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 text-sm rounded-full ${
                          tag === "vip"
                            ? "bg-purple-100 text-purple-700"
                            : tag === "problem"
                            ? "bg-red-100 text-red-700"
                            : tag === "damage"
                            ? "bg-rose-100 text-rose-700"
                            : tag === "chargeback"
                            ? "bg-orange-100 text-orange-700"
                            : tag === "no-show"
                            ? "bg-yellow-100 text-yellow-700"
                            : tag === "first-time"
                            ? "bg-green-100 text-green-700"
                            : tag === "corporate"
                            ? "bg-blue-100 text-blue-700"
                            : tag === "repeated"
                            ? "bg-orange-100 text-orange-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">No tags assigned</span>
                  )}
                </div>
              </div>

              {/* Status */}
              {selectedCustomer.isBlacklisted && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 text-red-700">
                    <Icon as={FiAlertCircle} size={18} />
                    <span className="font-medium">This customer is blacklisted</span>
                  </div>
                  {selectedCustomer.blacklistReason && (
                    <p className="mt-2 text-sm text-red-600">
                      Reason: {selectedCustomer.blacklistReason}
                    </p>
                  )}
                </div>
              )}

              {/* Delete Button */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleDeleteCustomer(selectedCustomer);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Icon as={FiTrash2} size={16} />
                  Delete Customer Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag Management Modal */}
      {showTagModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Manage Tags</h2>
              <button
                onClick={() => setShowTagModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon as={FiX} size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Select tags for <span className="font-medium">{getCustomerName(selectedCustomer)}</span>
              </p>

              <div className="space-y-2">
                {AVAILABLE_TAGS.map((tag) => (
                  <label
                    key={tag}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTags.includes(tag)
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={() => handleToggleTag(tag)}
                      className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                    />
                    <span className={`px-2 py-1 text-sm rounded-full ${
                      tag === "vip"
                        ? "bg-purple-100 text-purple-700"
                        : tag === "problem"
                        ? "bg-red-100 text-red-700"
                        : tag === "damage"
                        ? "bg-rose-100 text-rose-700"
                        : tag === "chargeback"
                        ? "bg-orange-100 text-orange-700"
                        : tag === "no-show"
                        ? "bg-yellow-100 text-yellow-700"
                        : tag === "first-time"
                        ? "bg-green-100 text-green-700"
                        : tag === "corporate"
                        ? "bg-blue-100 text-blue-700"
                        : tag === "repeated"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-700"
                    }`}>
                      {tag}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowTagModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTags}
                  disabled={savingTags}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                >
                  {savingTags ? 'Saving...' : 'Save Tags'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && noteCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Add Note</h2>
              <button
                onClick={() => setShowNoteModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon as={FiX} size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Note for <span className="font-medium">{getCustomerName(noteCustomer)}</span>
              </p>
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value as CustomerNote['type'])}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="general">General</option>
                <option value="warning">Warning</option>
                <option value="positive">Positive</option>
                <option value="communication">Communication</option>
              </select>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="w-full min-h-[120px] p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Write your note..."
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={savingNote || !noteText.trim()}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                >
                  {savingNote ? 'Saving...' : 'Add Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-red-600 flex items-center gap-2">
                <Icon as={FiAlertCircle} size={20} />
                Delete Customer
              </h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon as={FiX} size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-bold">
                    {getCustomerInitials(selectedCustomer)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{getCustomerName(selectedCustomer)}</p>
                  <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-700 font-medium mb-2">Warning: This action cannot be undone!</p>
                <p className="text-sm text-red-600">
                  This will permanently delete the customer from the database. 
                  {(selectedCustomer.bookingCount || 0) > 0 && (
                    <span className="block mt-1">
                      Note: This customer has {selectedCustomer.bookingCount} booking(s) associated with them.
                    </span>
                  )}
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to permanently delete this customer?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCustomer}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Icon as={FiTrash2} size={16} />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
